<template>
  <BaseModal :name="name" @onOpen="onOpen" @onClose="onClose">
    <template #dialog-title>
      <h2 class="text-center text-2xl font-bold">
        {{ $t('request-form.title') }}
      </h2>
    </template>
    <template #default="{ closeModal }">
      <div class="w-[640px]">
        <div
          v-if="requestForm.isSuccess.value || requestForm.isError.value"
          class="flex items-center justify-center px-4 py-8"
        >
          <RequestSuccess
            v-if="requestForm.isSuccess.value"
            class="w-4/5"
            @onProceed="closeModal"
          />
          <RequestError
            v-else
            message="Error"
            class="w-4/5"
            @onRetry="requestForm.resetCreateRequest"
          />
        </div>
        <form
          v-else
          class="flex flex-col gap-3 px-2 pt-6"
          @submit.prevent="onSubmit"
        >
          <AuiInput
            v-maska
            data-maska="Z"
            :data-maska-tokens="masksLib.nameMask"
            :clearable="false"
            :value="requestForm.state.name"
            :label="$t('request-form.form.name.placeholder')"
            :is-error="requestForm.validation$.value.name.$error"
            :disabled="requestForm.isLoading.value"
            @maska="onNameChange"
            @blur="logOnNameEnter"
          >
            <template
              v-if="requestForm.validation$.value.name.$error"
              #description
            >
              {{ requestForm.validation$.value.name.$errors[0]?.$message }}
            </template>
          </AuiInput>
          <div class="grid grid-cols-2 gap-3">
            <ContactMethodDropdown
              :selected-method="requestForm.state.contact_method"
              :is-disabled="requestForm.isLoading.value"
              :contact-methods="requestForm.contactMethods"
              @onSelect="onContactMethodSelect"
            />
            <AuiInput
              v-maska
              data-maska="+7 (###) ###-##-##"
              type="tel"
              :clearable="false"
              :value="requestForm.state.phone"
              :label="$t('request-form.form.phone.placeholder')"
              :is-error="requestForm.validation$.value.phone.$error"
              :disabled="requestForm.isLoading.value"
              @maska="onPhoneChange"
              @blur="logOnPhoneEnter"
            >
              <template
                v-if="requestForm.validation$.value.phone.$error"
                #description
              >
                {{
                  requestForm.validation$.value.phone.$errors[0]?.$message.toString()
                }}
              </template>
            </AuiInput>
          </div>
          <AuiButton
            color="alt"
            size="XXL"
            :disabled="requestForm.isLoading.value"
          >
            <UiSpinner
              v-if="requestForm.isLoading.value"
              class="inline-block text-white"
            />
            <span v-else>
              {{ $t('request-form.submit') }}
            </span>
          </AuiButton>
        </form>
      </div>
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
  import { vMaska } from 'maska';
  import { AuiButton, AuiInput } from '@aviata/auiata';

  import { ContactMethod } from '@entities/contact-methods';

  import { UiSpinner } from '@shared/ui';
  import { BaseModal } from '@shared/modal';
  import { masksLib } from '@shared/lib';
  import { Analytics } from '@shared/analytics';
  import { useRequestForm } from '../../model';
  import { requestFormConfig } from '../../config';
  import RequestSuccess from '../RequestSuccess.vue';
  import RequestError from '../RequestError.vue';
  import ContactMethodDropdown from './ContactMethodDropdown.vue';
  import type { TourData, RequestSource } from '../../types';

  const props = defineProps<{
    name: string;
    source: RequestSource;
    tourData?: TourData;
  }>();

  const requestForm = useRequestForm(props.source);

  /**
   * @description We need to use type cast since event value's
   * type may be null
   */
  const withTypeSafe = (fn: (val: string) => void) => {
    return (e: Event) => {
      const target = e.target as HTMLInputElement;
      fn(target.value);
    };
  };

  const onNameChange = withTypeSafe(requestForm.onNameChange);
  const onPhoneChange = withTypeSafe(requestForm.onPhoneChange);

  const logOnNameEnter = () => {
    Analytics.logEvent({
      event: requestFormConfig.analyticsEvents.ENTER_NAME,
    });
  };

  const logOnPhoneEnter = () => {
    Analytics.logEvent({
      event: requestFormConfig.analyticsEvents.ENTER_PHONE,
    });
  };

  const onContactMethodSelect = (contactMethod: ContactMethod) => {
    requestForm.onContactMethodChange(contactMethod);

    Analytics.logEvent({
      event: requestFormConfig.analyticsEvents.SELECT_CONTACT_TYPE,
      action: contactMethod,
    });
  };

  const onSubmit = () => {
    requestForm.submit(props.tourData);

    Analytics.logEvent({
      event: requestFormConfig.analyticsEvents.SUBMIT.CLICK,
    });
  };

  const onOpen = () => {
    Analytics.logEvent({
      event: requestFormConfig.analyticsEvents.OPEN,
    });
  };

  const onClose = () => {
    requestForm.reset();

    Analytics.logEvent({
      event: requestFormConfig.analyticsEvents.CLOSE,
    });
  };
</script>
